import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/api/client';
import { useTenantStore } from '@/stores/tenantStore';

import { sessionKeys } from './sessionKeys';
import { inviteKeys } from './useListWorkflowInvites';

/**
 * Sends ONE already-recorded invite its link again — the nudge for a recipient who
 * never turned up.
 *
 * This is the only route by which a person receives the link twice: a bulk or
 * single Send skips anyone already invited to the workflow, so duplicates can never
 * arrive by accident. No new invite row is written and the tracking token is
 * unchanged, so the Sent list keeps one line per person and whichever copy of the
 * email they open still attributes to the same row.
 */
export function useResendWorkflowInvite() {
  const qc = useQueryClient();
  const tenantId = useTenantStore((s) => s.tenantId);

  return useMutation({
    mutationFn: async (inviteId: string) => {
      await apiClient.post(`api/v1/t/${tenantId}/invites/${inviteId}/resend`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: inviteKeys.all });
      // Unopened invites are rows in the SESSIONS list now, so that is what has to
      // refresh for the Sent date to move on screen.
      qc.invalidateQueries({ queryKey: sessionKeys.lists() });
    },
  });
}
